/**
 * Phase 20 Plan 20-02 — Phone number zod schema.
 *
 * Mirrors backend PhoneNumberValidator (D-190):
 *   - Strip whitespace, +, -, (, ) from input.
 *   - Remaining chars must ALL be digits.
 *   - Total digit count: 8-20.
 *   - Country code: 1-5 digits; local number: 7-19 digits.
 *     Regex splits as: first 1-5 digits = CC, remaining = local.
 */

import { z } from 'zod';

/** Regex: group 1 = country code (1-5 digits), group 2 = local number (7-19 digits). */
const PHONE_RE = /^(\d{1,5})(\d{7,19})$/;

/**
 * Validates a raw phone input string (e.g. "+91 98765-43210").
 * On success, outputs the stripped digit-only string.
 */
export const phoneInputSchema = z
  .string()
  .min(1, 'Phone number is required')
  .transform((v) => v.replace(/[\s+\-()]/g, ''))
  .refine((v) => /^\d+$/u.test(v), { message: 'Phone must contain digits only' })
  .refine((v) => v.length >= 8 && v.length <= 20, {
    message: 'Phone must be 8 to 20 digits total (country code + number)',
  })
  .refine((v) => PHONE_RE.test(v), {
    message: 'Country code 1-5 digits + local number 7-19 digits',
  });

/**
 * Validates AND splits the phone into { countryCode, localNumber }.
 * Useful for the submit handler — produces the exact fields the backend expects.
 */
export const phoneSplitSchema = phoneInputSchema.transform((v) => {
  const m = PHONE_RE.exec(v);
  if (m?.[1] === undefined || m[2] === undefined) {
    throw new Error('Invalid phone after validation');
  }
  return { countryCode: m[1], localNumber: m[2] };
});
